import { Link } from "react-router-dom";
import {
    ArrowRight,
    Check,
    MessageCircle,
    Phone,
} from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { PageHero } from "@/components/PageHero";
import { IMG, PHONE_DISPLAY, TEL_URL, WA_URL } from "@/lib/site";

const SERVICE_SECTIONS = [
    {
        num: "01",
        title: "ELEVATOR INSTALLATION",
        heading: "Professional lift installation for your property",
        text: "We provide professional installation solutions for suitable residential and commercial projects — planned carefully and completed with a focus on safety and reliable operation.",
        points: [
            "Site assessment",
            "Installation planning",
            "Equipment installation",
            "Testing",
            "Commissioning",
            "Safety checks",
        ],
        cta: { label: "DISCUSS YOUR PROJECT", to: "/contact", testid: "service-cta-installation" },
        img: IMG.capsule,
        alt: "Panoramic glass capsule elevators ascending a high-rise atrium",
    },
    {
        num: "02",
        title: "MAINTENANCE & AMC",
        heading: "Preventive maintenance that prevents problems",
        text: "Regular servicing and preventive maintenance keep your elevator operating safely and smoothly. Our AMC support is structured around routine care, not reactive fixes.",
        points: [
            "Routine inspection",
            "Preventive maintenance",
            "Safety checks",
            "Component inspection",
            "Lubrication",
            "Performance checks",
            "AMC support",
        ],
        cta: { label: "REQUEST MAINTENANCE", to: "/contact", testid: "service-cta-maintenance" },
        img: IMG.caliper,
        alt: "Engineer performing precision measurement during technical inspection",
    },
];

const REPAIR_POINTS = [
    "Elevator fault diagnosis",
    "Component inspection",
    "Repair",
    "Replacement support",
    "System troubleshooting",
    "Operational testing",
];

export default function Services() {
    return (
        <>
            <Seo
                title="Elevator Services — Installation, Maintenance, AMC & Breakdown | K1 ELEVATOR'S Patna"
                description="Lift installation, elevator maintenance, AMC, breakdown service and repair in Patna, Gaya and Ranchi. Multi-brand elevator service support by K1 ELEVATOR'S. Call +91 89694 49945."
            />

            <PageHero
                eyebrow="Our Services"
                title="ELEVATOR SERVICES YOU CAN RELY ON"
                subtitle="Installation, maintenance and breakdown support for dependable elevator performance."
                image={IMG.shaft}
                testid="services-hero-heading"
            />

            {/* Services 01 & 02 */}
            <section className="mx-auto max-w-7xl space-y-24 px-5 py-24 sm:px-8">
                {SERVICE_SECTIONS.map((s, idx) => (
                    <div
                        key={s.num}
                        className={`grid items-center gap-12 lg:grid-cols-2 ${
                            idx % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
                        }`}
                    >
                        <Reveal className="relative">
                            <div className="overflow-hidden rounded-3xl shadow-[0_24px_60px_rgb(11,21,40,0.16)]">
                                <img
                                    src={s.img}
                                    alt={s.alt}
                                    loading="lazy"
                                    className="aspect-[4/3] w-full object-cover"
                                />
                            </div>
                            <span className="absolute -top-6 left-6 rounded-xl bg-[#0B1528] px-5 py-3 text-2xl font-extrabold text-[#60A5FA] shadow-xl">
                                {s.num}
                            </span>
                        </Reveal>
                        <div>
                            <Reveal>
                                <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#2563EB]">
                                    Service {s.num}
                                </p>
                                <h2 className="mt-3 text-2xl font-extrabold tracking-tight text-[#0B1528] sm:text-3xl">
                                    {s.title}
                                </h2>
                                <p className="mt-2 text-lg font-semibold text-slate-700">{s.heading}</p>
                                <p className="mt-3 text-sm leading-relaxed text-slate-600 sm:text-base">
                                    {s.text}
                                </p>
                            </Reveal>
                            <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                                {s.points.map((p, i) => (
                                    <Reveal key={p} delay={i * 50} className="flex items-center gap-2.5">
                                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#F0F5FD] text-[#1D4ED8]">
                                            <Check className="h-3.5 w-3.5" />
                                        </span>
                                        <span className="text-sm font-semibold text-slate-700">{p}</span>
                                    </Reveal>
                                ))}
                            </ul>
                            <Reveal delay={180}>
                                <Link
                                    to={s.cta.to}
                                    data-testid={s.cta.testid}
                                    className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#1D4ED8] px-6 py-3 text-sm font-bold text-white shadow-[0_4px_16px_rgba(37,99,235,0.35)] transition-[background-color,box-shadow] duration-200 hover:bg-[#1E40AF]"
                                >
                                    {s.cta.label} <ArrowRight className="h-4 w-4" />
                                </Link>
                            </Reveal>
                        </div>
                    </div>
                ))}
            </section>

            {/* Service 03 — Breakdown (prominent) */}
            <section className="relative overflow-hidden bg-[#0B1528] py-24">
                <div
                    className="absolute inset-0 opacity-[0.12]"
                    style={{
                        backgroundImage:
                            "linear-gradient(rgba(96,165,250,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(96,165,250,0.5) 1px, transparent 1px)",
                        backgroundSize: "44px 44px",
                    }}
                />
                <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-2">
                    <div>
                        <Reveal>
                            <p className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.25em] text-[#93C5FD]">
                                <span className="pulse-dot h-2 w-2 rounded-full bg-[#60A5FA]" />
                                Service 03 — Breakdown &amp; Emergency
                            </p>
                            <h2 className="mt-5 text-3xl font-extrabold leading-tight tracking-tight text-white sm:text-4xl">
                                ELEVATOR STOPPED?
                                <br />
                                <span className="text-[#60A5FA]">WE&rsquo;RE READY TO HELP.</span>
                            </h2>
                            <p className="mt-4 max-w-lg text-sm leading-relaxed text-slate-300 sm:text-base">
                                K1 ELEVATOR&rsquo;S provides breakdown troubleshooting and repair
                                support to help restore elevator operation.
                            </p>
                        </Reveal>
                        <ul className="mt-7 grid gap-3 sm:grid-cols-2">
                            {[
                                "Fault diagnosis",
                                "Troubleshooting",
                                "Repair",
                                "Component replacement",
                                "Performance testing",
                            ].map((p, i) => (
                                <Reveal key={p} delay={i * 50} className="flex items-center gap-2.5">
                                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#2563EB]/20 text-[#60A5FA]">
                                        <Check className="h-3.5 w-3.5" />
                                    </span>
                                    <span className="text-sm font-semibold text-slate-200">{p}</span>
                                </Reveal>
                            ))}
                        </ul>
                        <Reveal delay={180}>
                            <p className="mt-9 text-xs font-bold uppercase tracking-[0.25em] text-slate-400">
                                Direct Breakdown Line
                            </p>
                            <a
                                href={TEL_URL}
                                data-testid="services-breakdown-phone"
                                className="mt-2 block text-3xl font-extrabold tracking-tight text-white transition-colors duration-200 hover:text-[#60A5FA] sm:text-4xl"
                            >
                                CALL {PHONE_DISPLAY}
                            </a>
                            <div className="mt-6 flex flex-wrap gap-4">
                                <a
                                    href={TEL_URL}
                                    data-testid="services-breakdown-call-btn"
                                    className="inline-flex items-center gap-2 rounded-full bg-[#2563EB] px-7 py-3.5 text-sm font-bold text-white shadow-[0_4px_20px_rgba(37,99,235,0.45)] transition-colors duration-200 hover:bg-[#1D4ED8]"
                                >
                                    <Phone className="h-4 w-4" /> CALL NOW
                                </a>
                                <a
                                    href={WA_URL}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="services-breakdown-whatsapp-btn"
                                    className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-7 py-3.5 text-sm font-bold text-white backdrop-blur-md transition-[background-color,border-color] duration-200 hover:border-white/60 hover:bg-white/20"
                                >
                                    <MessageCircle className="h-4 w-4" /> WHATSAPP US
                                </a>
                            </div>
                        </Reveal>
                    </div>
                    <Reveal delay={120}>
                        <div className="overflow-hidden rounded-3xl border border-white/10 shadow-[0_24px_60px_rgb(0,0,0,0.4)]">
                            <img
                                src={IMG.machinery}
                                alt="Engineer calibrating elevator machinery under inspection lighting"
                                loading="lazy"
                                className="aspect-[4/3] w-full object-cover"
                            />
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* Service 04 — Multi-brand */}
            <section className="bg-[#F0F5FD] py-24">
                <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-2">
                    <Reveal className="relative lg:order-2">
                        <div className="overflow-hidden rounded-3xl shadow-[0_24px_60px_rgb(11,21,40,0.16)]">
                            <img
                                src={IMG.team}
                                alt="Elevator engineering team planning service work on site"
                                loading="lazy"
                                className="aspect-[4/3] w-full object-cover"
                            />
                        </div>
                        <span className="absolute -top-6 left-6 rounded-xl bg-[#0B1528] px-5 py-3 text-2xl font-extrabold text-[#60A5FA] shadow-xl">
                            04
                        </span>
                    </Reveal>
                    <div className="lg:order-1">
                        <Reveal>
                            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#2563EB]">
                                Service 04
                            </p>
                            <h2 className="mt-3 text-2xl font-extrabold tracking-tight text-[#0B1528] sm:text-3xl">
                                MAINTENANCE FOR DIFFERENT ELEVATOR BRANDS
                            </h2>
                            <p className="mt-4 text-sm leading-relaxed text-slate-600 sm:text-base">
                                K1 ELEVATOR&rsquo;S provides maintenance and service support for
                                elevators from different manufacturers, subject to the equipment and
                                service requirements of each installation.
                            </p>
                            <p className="mt-3 text-sm leading-relaxed text-slate-600 sm:text-base">
                                Every elevator is assessed on its own condition and technical needs,
                                so the service approach matches the equipment in your building.
                            </p>
                        </Reveal>
                        <Reveal delay={150}>
                            <Link
                                to="/contact"
                                data-testid="service-cta-multibrand"
                                className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#1D4ED8] px-6 py-3 text-sm font-bold text-white shadow-[0_4px_16px_rgba(37,99,235,0.35)] transition-colors duration-200 hover:bg-[#1E40AF]"
                            >
                                ASK ABOUT YOUR ELEVATOR <ArrowRight className="h-4 w-4" />
                            </Link>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* Service 05 — Repair */}
            <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
                <div className="grid items-center gap-12 lg:grid-cols-2">
                    <Reveal className="relative">
                        <div className="overflow-hidden rounded-3xl shadow-[0_24px_60px_rgb(11,21,40,0.16)]">
                            <img
                                src={IMG.cabin}
                                alt="Premium elevator cabin interior after service"
                                loading="lazy"
                                className="aspect-[4/3] w-full object-cover"
                            />
                        </div>
                        <span className="absolute -top-6 left-6 rounded-xl bg-[#0B1528] px-5 py-3 text-2xl font-extrabold text-[#60A5FA] shadow-xl">
                            05
                        </span>
                    </Reveal>
                    <div>
                        <Reveal>
                            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#2563EB]">
                                Service 05
                            </p>
                            <h2 className="mt-3 text-2xl font-extrabold tracking-tight text-[#0B1528] sm:text-3xl">
                                REPAIR &amp; TROUBLESHOOTING
                            </h2>
                            <p className="mt-4 text-sm leading-relaxed text-slate-600 sm:text-base">
                                When an elevator develops faults — unusual noise, jerky movement, door
                                issues or complete stoppage — we diagnose the problem and carry out
                                practical repairs focused on restoring safe operation.
                            </p>
                        </Reveal>
                        <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                            {REPAIR_POINTS.map((p, i) => (
                                <Reveal key={p} delay={i * 50} className="flex items-center gap-2.5">
                                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#F0F5FD] text-[#1D4ED8]">
                                        <Check className="h-3.5 w-3.5" />
                                    </span>
                                    <span className="text-sm font-semibold text-slate-700">{p}</span>
                                </Reveal>
                            ))}
                        </ul>
                        <Reveal delay={180}>
                            <Link
                                to="/contact"
                                data-testid="service-cta-repair"
                                className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#1D4ED8] px-6 py-3 text-sm font-bold text-white shadow-[0_4px_16px_rgba(37,99,235,0.35)] transition-colors duration-200 hover:bg-[#1E40AF]"
                            >
                                REQUEST A REPAIR VISIT <ArrowRight className="h-4 w-4" />
                            </Link>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-[#0E1E38] py-20">
                <div className="mx-auto max-w-4xl px-5 text-center sm:px-8">
                    <Reveal>
                        <h2 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
                            KEEP YOUR ELEVATOR MOVING
                        </h2>
                        <p className="mx-auto mt-4 max-w-xl text-base text-slate-300">
                            Talk to K1 ELEVATOR&rsquo;S about installation, maintenance or repair.
                        </p>
                        <Link
                            to="/contact"
                            data-testid="services-cta-contact-btn"
                            className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#2563EB] px-8 py-3.5 text-sm font-bold text-white shadow-[0_4px_20px_rgba(37,99,235,0.45)] transition-colors duration-200 hover:bg-[#1D4ED8]"
                        >
                            CONTACT US <ArrowRight className="h-4 w-4" />
                        </Link>
                    </Reveal>
                </div>
            </section>
        </>
    );
}
